import React, { useState, useEffect, useCallback } from 'react';
import { supplierOrderService, productService, proveedorService, extractErrorDetails } from '../services/api';
import BarcodeScanner from './BarcodeScanner';
import ErrorDisplay from './ErrorDisplay';

function CreateSupplierOrder({ onOrderCreated }) {
  const [descripcion, setDescripcion] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingProveedores, setLoadingProveedores] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [success, setSuccess] = useState('');

  // Load all active products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await productService.getAllActiveProducts();
        if (response.success) {
          setAllProducts(response.data || []);
        }
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  // Load all active proveedores on mount
  useEffect(() => {
    const loadProveedores = async () => {
      try {
        setLoadingProveedores(true);
        const response = await proveedorService.getActiveProveedores();
        if (response.success) {
          setProveedores(response.data || []);
        }
      } catch (err) {
        console.error('Error loading proveedores:', err);
      } finally {
        setLoadingProveedores(false);
      }
    };
    loadProveedores();
  }, []);

  // Local search filter - filters from preloaded products
  const handleSearch = useCallback((query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const filtered = allProducts.filter(product => 
      product.nombre.toLowerCase().includes(lowerQuery) ||
      product.codigoBarras.toLowerCase().includes(lowerQuery)
    );
    setSearchResults(filtered.slice(0, 10)); // Limit to 10 results
  }, [allProducts]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  const handleScan = async (barcode) => {
    setShowScanner(false);
    setError('');
    setErrorTrace(null);
    try {
      const response = await productService.getProductByBarcode(barcode);
      if (response.success && response.data) {
        addProductToOrder(response.data);
      } else {
        setError(response.message || 'Producto no encontrado con ese código de barras');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Producto no encontrado con ese código de barras');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const addProductToOrder = (product) => {
    const existingIndex = items.findIndex(item => item.productId === product.id);
    
    if (existingIndex >= 0) {
      // Increment quantity if already exists
      const newItems = [...items];
      newItems[existingIndex].cantidad += 1;
      setItems(newItems);
    } else {
      // Add new item
      setItems([...items, {
        productId: product.id,
        codigoBarras: product.codigoBarras,
        nombreProducto: product.nombre,
        cantidad: 1
      }]);
    }
    
    setSearchQuery('');
    setSearchResults([]);
    setError('');
  };

  const updateItemQuantity = (index, cantidad) => {
    const newItems = [...items];
    newItems[index].cantidad = parseInt(cantidad, 10) || 1;
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorTrace(null);
    setSuccess('');

    if (!proveedorId) {
      setError('Debe seleccionar un proveedor');
      return;
    }

    if (!descripcion.trim()) {
      setError('Debe ingresar una descripción');
      return;
    }

    if (items.length === 0) {
      setError('Debe agregar al menos un producto a la orden');
      return;
    }

    try {
      setLoading(true);
      const response = await supplierOrderService.createSupplierOrder({
        descripcion: descripcion.trim(),
        proveedorId: parseInt(proveedorId, 10),
        items: items
      });

      if (response.success) {
        setSuccess('Orden al proveedor creada exitosamente');
        // Clear form
        setDescripcion('');
        setProveedorId('');
        setItems([]);
        setSearchQuery('');
        setSearchResults([]);
        
        if (onOrderCreated) {
          setTimeout(() => onOrderCreated(response.data), 1000);
        }
      } else {
        setError(response.message || 'Error al crear la orden');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al crear la orden');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setDescripcion('');
    setProveedorId('');
    setItems([]);
    setSearchQuery('');
    setSearchResults([]);
    setError('');
    setErrorTrace(null);
    setSuccess('');
  };

  return (
    <div className="create-order-container">
      {showScanner && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      <div className="create-order-layout">
        {/* Product Search Section */}
        <div className="product-search-panel">
          <h3>Buscar Productos</h3>
          
          {loadingProducts ? (
            <p className="loading-text">Cargando productos...</p>
          ) : (
            <>
              <div className="search-input-container">
                <div className="input-with-button">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por código o nombre..."
                    className="search-input"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowScanner(true)}
                    className="btn btn-icon"
                    title="Escanear código de barras"
                  >
                    📷
                  </button>
                </div>
              </div>

              {searchResults.length > 0 && (
                <div className="product-results">
                  <h4>Resultados ({searchResults.length})</h4>
                  <ul className="product-list">
                    {searchResults.map(product => (
                      <li 
                        key={product.id}
                        onClick={() => addProductToOrder(product)}
                        className="product-item"
                      >
                        <span className="product-code">{product.codigoBarras}</span>
                        <span className="product-name">{product.nombre}</span>
                        <button 
                          type="button" 
                          className="btn btn-small btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            addProductToOrder(product);
                          }}
                        >
                          + Agregar
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="no-results">No se encontraron productos</p>
              )}

              {searchQuery.length === 0 && (
                <p className="search-hint">Escriba al menos 2 caracteres para buscar</p>
              )}
            </>
          )}
        </div>

        {/* Order Form Section */}
        <div className="order-form-panel">
          <h3>Orden al Proveedor</h3>
          
          <form onSubmit={handleSubmit} className="order-form">
            <div className="input-group">
              <label htmlFor="proveedor">Proveedor*</label>
              {loadingProveedores ? (
                <p className="loading-text">Cargando proveedores...</p>
              ) : (
                <select
                  id="proveedor"
                  name="proveedor"
                  value={proveedorId}
                  onChange={(e) => setProveedorId(e.target.value)}
                  required
                >
                  <option value="">Seleccione un proveedor</option>
                  {proveedores.map(proveedor => (
                    <option key={proveedor.id} value={proveedor.id}>
                      {proveedor.nombre}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="input-group">
              <label htmlFor="descripcion">Descripción de la Orden*</label>
              <textarea
                id="descripcion"
                name="descripcion"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Descripción de la solicitud (ej: Pedido urgente, Reposición semanal, etc.)"
                rows="2"
                required
              />
            </div>

            {items.length > 0 ? (
              <div className="order-items">
                <div className="order-items-header">
                  <h4>Productos en la Orden ({items.length})</h4>
                  <span className="total-units">
                    Total: {items.reduce((sum, item) => sum + item.cantidad, 0)} unidades
                  </span>
                </div>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Producto</th>
                      <th>Cantidad</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.codigoBarras}</td>
                        <td>{item.nombreProducto}</td>
                        <td>
                          <input
                            type="number"
                            value={item.cantidad}
                            onChange={(e) => updateItemQuantity(index, e.target.value)}
                            onBlur={(e) => {
                              // Ensure valid quantity on blur
                              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                                updateItemQuantity(index, 1);
                              }
                            }}
                            min="1"
                            className="quantity-input"
                          />
                        </td>
                        <td>
                          <button 
                            type="button"
                            onClick={() => removeItem(index)}
                            className="btn btn-small btn-danger"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-cart">
                <p>No hay productos en la orden</p>
                <p className="hint">Use la búsqueda para agregar productos</p>
              </div>
            )}

            <ErrorDisplay error={error} errorTrace={errorTrace} />
            {success && <p className="success-message">{success}</p>}

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={clearForm}
                disabled={loading}
              >
                Limpiar
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || items.length === 0}
              >
                {loading ? 'Creando...' : 'Crear Orden'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateSupplierOrder;
